import { initDetailsValues } from "../../../RFP/coverages/CoverageForms/coverages";
import { CoverageType } from "../coverageGrid";

import { FORM_STEP, PLAN_TYPE } from "./constants";
import {
  Action,
  CoverageDetails,
  Details,
  NextStepDetails,
  ProductElement,
  SelectedCoverageDetails,
  State,
} from "./types";
import {
  getAllSelectedCoverages,
  findNextStepDetails,
  createNextStepDetails,
  getNextStepDetailsForMultiPlans,
  updateSelectedCoveragesWithCoverage,
  isCoverageInvalid,
  getCurrentCoverageIndex,
} from "./helpers";

// initDetailsValues comes from an untyped .js module; every init object it
// returns carries a `plans` array, which is what Details requires.
const isDetails = (value: unknown): value is Details =>
  typeof value === "object" &&
  value !== null &&
  "plans" in value &&
  Array.isArray(value.plans);

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_STEP": {
      return handleSetStep(state, action.payload);
    }
    case "TOGGLE_COVERAGE": {
      return handleToggleCoverage(state, action.payload);
    }
    case "SET_SELECTED_COVERAGE_DETAILS": {
      return handleSetSelectedCoverageDetails(state, action.payload);
    }
    case "UPDATE_COVERAGE_DETAILS": {
      return handleUpdateCoverageDetails(state, action.payload);
    }
    case "SET_CURRENT_PLAN_INDEX": {
      return handleSetCurrentPlanIndex(state, action.payload);
    }
    case "UPDATE_SELECTED_COVERAGES": {
      return handleUpdateSelectedCoverages(state, action.payload);
    }
    case "NAVIGATE_TO_PREVIOUS_COVERAGE": {
      return handleNavigateToPreviousCoverage(state);
    }
    case "NAVIGATE_TO_NEXT_COVERAGE": {
      return handleNavigateToNextCoverage(state);
    }
    default:
      return state;
  }
}

function handleSetStep(state: State, payload: number): State {
  let currentCoverageDetails = state.currentCoverageDetails;
  let nextStepDetails = state.nextStepDetails;
  if (payload === FORM_STEP) {
    const coverages = getAllSelectedCoverages(state.selectedCoverages);
    currentCoverageDetails = coverages[0];
    // Find the first invalid coverage to set as nextStepDetails
    // If all coverages are valid, nextStepDetails should be null
    nextStepDetails = findNextStepDetails(
      coverages,
      currentCoverageDetails?.id || -1,
    );
  }
  return {
    ...state,
    currentStep: payload,
    currentCoverageDetails,
    nextStepDetails,
  };
}

function handleToggleCoverage(
  state: State,
  payload: { coverage: ProductElement; type: CoverageType },
): State {
  const { coverage, type } = payload;

  const selectedCoveragesByType = state.selectedCoverages[type];
  const coverageIndex = selectedCoveragesByType.findIndex(
    (cov) => cov.id === coverage.id,
  );

  let currentCoverageDetails = state.currentCoverageDetails;
  let nextStepDetails = state.nextStepDetails;
  let selectedCoverages: SelectedCoverageDetails;
  let updateNextCoverageDetails = false;
  // Remove coverage if exists
  if (coverageIndex > -1) {
    const coveragesByType = selectedCoveragesByType.toSpliced(coverageIndex, 1);
    selectedCoverages = {
      ...state.selectedCoverages,
      [type]: coveragesByType,
    };
    const coverages = getAllSelectedCoverages(selectedCoverages);
    if (coverage.id === currentCoverageDetails?.id) {
      currentCoverageDetails =
        coverages.find((cov) => !cov.isValid) ?? coverages[0];
      if (currentCoverageDetails) {
        updateNextCoverageDetails = true;
      }
    } else if (coverage.id === nextStepDetails?.id) {
      updateNextCoverageDetails = true;
    }

    if (updateNextCoverageDetails && currentCoverageDetails) {
      nextStepDetails = findNextStepDetails(
        coverages,
        currentCoverageDetails.id,
      );
    }
    // Add coverage if it doesn't exist
  } else {
    const initialDetails: unknown = initDetailsValues(coverage.name);
    const newCoverage = {
      ...coverage,
      isValid: false,
      details: isDetails(initialDetails) ? initialDetails : undefined,
    };
    const coveragesByType = selectedCoveragesByType.concat(newCoverage);
    selectedCoverages = {
      ...state.selectedCoverages,
      [type]: coveragesByType,
    };
    if (!currentCoverageDetails) {
      currentCoverageDetails = newCoverage;
    } else if (!nextStepDetails) {
      nextStepDetails = createNextStepDetails(newCoverage);
    }
  }

  return {
    ...state,
    currentCoverageDetails,
    nextStepDetails,
    selectedCoverages,
  };
}

function handleSetSelectedCoverageDetails(
  state: State,
  payload: NextStepDetails | null,
): State {
  const coverages = getAllSelectedCoverages(state.selectedCoverages);
  // Find the next coverage details that is not valid and is not the current coverage details
  const nextStepDetails = findNextStepDetails(coverages, payload.id);
  // Search in selected coverages the new current coverage details
  const currentCoverageDetails = coverages.find((c) => c.id === payload.id);

  return {
    ...state,
    currentCoverageDetails,
    currentPlanIndex: 0,
    nextStepDetails,
  };
}

function handleUpdateCoverageDetails(
  state: State,
  payload: { coverage: CoverageDetails; currentPlanKey: number; field: string },
): State {
  let isInvalid = true;
  let nextStepDetails = getNextStepDetailsForMultiPlans(
    payload.coverage,
    payload.currentPlanKey,
  );

  if (!nextStepDetails) {
    isInvalid = isCoverageInvalid(payload.coverage);
    // If the current coverage details is the same as the next step details find the next
    // It means this is a coverage with multiple plans and next step is for the tab index
    // Coverage details that is not valid and is not the current coverage details
    if (payload.coverage.id === state.nextStepDetails?.id) {
      nextStepDetails = findNextStepDetails(
        getAllSelectedCoverages(state.selectedCoverages),
        payload.coverage.id,
      );
    }
  }

  const currentPlanIndex =
    payload.field === PLAN_TYPE ? 0 : state.currentPlanIndex;

  const currentCoverageDetails = {
    ...payload.coverage,
    isValid: !isInvalid,
  };

  const selectedCoverages = updateSelectedCoveragesWithCoverage(
    state.selectedCoverages,
    currentCoverageDetails,
  );

  return {
    ...state,
    currentPlanIndex,
    currentCoverageDetails,
    selectedCoverages,
    nextStepDetails: nextStepDetails ?? state.nextStepDetails,
  };
}

function handleSetCurrentPlanIndex(state: State, payload: number): State {
  let nextStepDetails = getNextStepDetailsForMultiPlans(
    state.currentCoverageDetails,
    payload,
  );

  // If there are multiple plans and there are no invalid plans left find the next
  // Coverage details that is not valid and is not the current coverage details
  if (
    state.currentCoverageDetails.details.plans.length > 1 &&
    !nextStepDetails
  ) {
    nextStepDetails = findNextStepDetails(
      getAllSelectedCoverages(state.selectedCoverages),
      state.currentCoverageDetails.id,
    );
  }
  return {
    ...state,
    nextStepDetails,
    currentPlanIndex: payload,
  };
}

function handleUpdateSelectedCoverages(
  state: State,
  payload: SelectedCoverageDetails,
): State {
  return {
    ...state,
    selectedCoverages: payload,
  };
}

function handleNavigateToPreviousCoverage(state: State): State {
  const coverages = getAllSelectedCoverages(state.selectedCoverages);
  const currentIndex = getCurrentCoverageIndex(
    coverages,
    state.currentCoverageDetails?.id,
  );

  // Don't navigate if we're at the first coverage
  if (currentIndex <= 0) {
    return state;
  }

  const previousIndex = currentIndex - 1;
  const previousCoverage = coverages[previousIndex];

  return {
    ...state,
    currentCoverageDetails: previousCoverage,
    currentPlanIndex: 0,
    nextStepDetails: findNextStepDetails(coverages, previousCoverage.id),
  };
}

function handleNavigateToNextCoverage(state: State): State {
  const coverages = getAllSelectedCoverages(state.selectedCoverages);
  const currentIndex = getCurrentCoverageIndex(
    coverages,
    state.currentCoverageDetails?.id,
  );

  // Don't navigate if we're at the last coverage
  if (currentIndex >= coverages.length - 1) {
    return state;
  }

  const nextIndex = currentIndex + 1;
  const nextCoverage = coverages[nextIndex];

  return {
    ...state,
    currentCoverageDetails: nextCoverage,
    currentPlanIndex: 0,
    nextStepDetails: findNextStepDetails(coverages, nextCoverage.id),
  };
}
